import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type KeyboardEvent,
} from "react";
import { useShowcaseInternal } from "./FeatureShowcaseProvider";
import type { ShowcaseSlide, ShowcaseAction } from "./types";

// ─── Optional motion (peer dep) ──────────────────────────────────────────

type FramerMotionModule = typeof import("motion/react");

let cachedModule: FramerMotionModule | null = null;
let loadPromise: Promise<FramerMotionModule | null> | null = null;

function loadFramerMotion(): Promise<FramerMotionModule | null> {
  if (cachedModule) return Promise.resolve(cachedModule);
  if (!loadPromise) {
    loadPromise = import("motion/react")
      .then((m) => { cachedModule = m; return m; })
      .catch(() => null);
  }
  return loadPromise;
}

function useFramerMotion() {
  const [mod, setMod] = useState<FramerMotionModule | null>(cachedModule);
  useEffect(() => {
    if (cachedModule) { setMod(cachedModule); return; }
    let cancelled = false;
    loadFramerMotion().then((m) => {
      if (!cancelled) setMod(m);
    });
    return () => { cancelled = true; };
  }, []);
  return mod;
}

// ─── Slide Renderers ────────────────────────────────────────────────────────────

interface DefaultSlideProps {
  slide: ShowcaseSlide;
  isActive: boolean;
}

function DefaultSlideContent({ slide }: DefaultSlideProps) {
  return (
    <div className="fso-slide">
      {slide.badge && (
        <span className="fso-slide__badge">{slide.badge}</span>
      )}
      {slide.media && <SlideMedia media={slide.media} />}
      <div className="fso-slide__body">
        <h2 className="fso-slide__title">{slide.title}</h2>
        {slide.description && (
          <p className="fso-slide__description">{slide.description}</p>
        )}
      </div>
      {slide.actions && slide.actions.length > 0 && (
        <div className="fso-slide__actions">
          {slide.actions.map((action, i) => (
            <SlideAction key={i} action={action} />
          ))}
        </div>
      )}
    </div>
  );
}

function SlideMedia({ media }: { media: ShowcaseSlide["media"] }) {
  if (!media) return null;

  switch (media.type) {
    case "image":
      return (
        <div className="fso-slide__media">
          <img
            src={media.src}
            alt={media.alt ?? ""}
            className="fso-slide__image"
            loading="lazy"
          />
        </div>
      );
    case "video":
      return (
        <div className="fso-slide__media">
          <video
            src={media.src}
            poster={media.poster}
            className="fso-slide__video"
            controls
            playsInline
            preload="metadata"
          >
            <track kind="captions" />
          </video>
        </div>
      );
    default:
      return null;
  }
}

function SlideAction({ action }: { action: ShowcaseAction }) {
  const className = `fso-action fso-action--${action.variant ?? "default"}`;

  if (action.href) {
    return (
      <a
        href={action.href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {action.label}
      </a>
    );
  }

  return (
    <button type="button" className={className} onClick={action.onClick}>
      {action.label}
    </button>
  );
}

// ─── Dot Indicators ─────────────────────────────────────────────────────────────

interface DotsProps {
  total: number;
  current: number;
  onSelect: (index: number) => void;
}

function DotIndicators({ total, current, onSelect }: DotsProps) {
  return (
    <div className="fso-dots" role="tablist" aria-label="Slide navigation">
      {Array.from({ length: total }, (_, i) => (
        <button
          key={i}
          type="button"
          role="tab"
          aria-selected={i === current}
          aria-label={`Go to slide ${i + 1}`}
          className={`fso-dot ${i === current ? "fso-dot--active" : ""}`}
          onClick={() => onSelect(i)}
        />
      ))}
    </div>
  );
}

// ─── Main Showcase Component ────────────────────────────────────────────────────

export interface FeatureShowcaseProps {
  /** Optional className for the overlay/backdrop */
  className?: string;
  /** Override default slide renderer */
  renderSlide?: (slide: ShowcaseSlide, index: number, isActive: boolean) => React.ReactNode;
  /** Override the close button */
  renderCloseButton?: (onClose: () => void) => React.ReactNode;
  /** Override navigation buttons */
  renderNavigation?: (props: {
    onNext: () => void;
    onPrevious: () => void;
    canGoNext: boolean;
    canGoPrevious: boolean;
    isLastSlide: boolean;
    currentIndex: number;
    totalSlides: number;
  }) => React.ReactNode;
}

export function FeatureShowcase({
  className,
  renderSlide,
  renderCloseButton,
  renderNavigation,
}: FeatureShowcaseProps) {
  const {
    activeFlow,
    isOpen,
    dismiss,
    markCompleted,
    currentIndex,
    setCurrentIndex,
  } = useShowcaseInternal();

  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [direction, setDirection] = useState(0);
  const framerMotion = useFramerMotion();

  // Focus management — focus overlay on open, restore on close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      // Defer focus so the dialog element is rendered first
      requestAnimationFrame(() => containerRef.current?.focus());
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Auto-advance
  useEffect(() => {
    if (!activeFlow?.autoAdvanceMs || activeFlow.autoAdvanceMs <= 0) return;
    if (!isOpen) return;

    const timer = setInterval(() => {
      const maxIndex = activeFlow.slides.length - 1;
      if (currentIndex < maxIndex) {
        setDirection(1);
        setCurrentIndex(currentIndex + 1);
      } else {
        markCompleted();
      }
    }, activeFlow.autoAdvanceMs);

    return () => clearInterval(timer);
  }, [activeFlow, isOpen, currentIndex, setCurrentIndex, markCompleted]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!activeFlow) return;

      const maxIndex = activeFlow.slides.length - 1;

      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          if (currentIndex < maxIndex) {
            setDirection(1);
            setCurrentIndex(currentIndex + 1);
          }
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          if (currentIndex > 0) {
            setDirection(-1);
            setCurrentIndex(currentIndex - 1);
          }
          break;
        case "Escape":
          if (activeFlow.dismissible !== false) {
            dismiss();
          }
          break;
      }
    },
    [activeFlow, currentIndex, setCurrentIndex, dismiss],
  );

  if (!isOpen || !activeFlow) return null;

  const slides = activeFlow.slides;
  const totalSlides = slides.length;
  const currentSlide = slides[currentIndex];
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < totalSlides - 1;
  const isLastSlide = currentIndex === totalSlides - 1;

  const handleNext = () => {
    if (isLastSlide) {
      markCompleted();
    } else {
      setDirection(1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious) {
      setDirection(-1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDotSelect = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // ─── Render slide content ──────────────────────────────────────────────
  const slideContent = renderSlide
    ? renderSlide(currentSlide, currentIndex, true)
    : <DefaultSlideContent slide={currentSlide} isActive={true} />;

  // ─── Wrap with motion AnimatePresence if available ─────────────
  const AnimatePresence = framerMotion?.AnimatePresence;
  const motion = framerMotion?.motion;

  const animatedSlide =
    motion && AnimatePresence ? (
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentSlide.id}
          custom={direction}
          initial={{ opacity: 0, x: direction * 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -60 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="fso-slide-container"
        >
          {slideContent}
        </motion.div>
      </AnimatePresence>
    ) : (
      <div className="fso-slide-container">{slideContent}</div>
    );

  return (
    <div
      className={`fso-overlay ${className ?? ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Feature showcase: ${activeFlow.flowId}`}
      onKeyDown={handleKeyDown}
      ref={containerRef}
      tabIndex={-1}
    >
      {/* Backdrop */}
      <div
        className="fso-backdrop"
        onClick={activeFlow.dismissible !== false ? dismiss : undefined}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div className="fso-modal">
        {/* Close button */}
        {activeFlow.dismissible !== false &&
          (renderCloseButton ? (
            renderCloseButton(dismiss)
          ) : (
            <button
              type="button"
              className="fso-close"
              onClick={dismiss}
              aria-label="Close showcase"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          ))}

        {/* Slide */}
        {animatedSlide}

        {/* Counter */}
        {activeFlow.showCounter && (
          <div className="fso-counter" aria-live="polite">
            {currentIndex + 1} of {totalSlides}
          </div>
        )}

        {/* Dot indicators */}
        {activeFlow.showIndicators !== false && totalSlides > 1 && (
          <DotIndicators
            total={totalSlides}
            current={currentIndex}
            onSelect={handleDotSelect}
          />
        )}

        {/* Navigation */}
        {renderNavigation ? (
          renderNavigation({
            onNext: handleNext,
            onPrevious: handlePrevious,
            canGoNext,
            canGoPrevious,
            isLastSlide,
            currentIndex,
            totalSlides,
          })
        ) : (
          <div className="fso-nav">
            <button
              type="button"
              className="fso-nav__btn fso-nav__btn--prev"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              aria-label="Previous slide"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>
            <button
              type="button"
              className="fso-nav__btn fso-nav__btn--next"
              onClick={handleNext}
              aria-label={isLastSlide ? "Complete" : "Next slide"}
            >
              {isLastSlide ? "Get Started" : "Next"}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
